/* Shelf Attribution Panel — Feb-2026.
 *
 * Admin-only heatmap of bookstore revenue grouped by Loom shelf heat
 * (mild / medium / hot / scorching) and bucket (dark / grey / sweet /
 * contemp). Answers the ops question: "Which subgenre should we
 * double down on for the next shelf run?"
 *
 * Mounted at /admin/shelf-attribution via the Admin router.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, TrendingUp, RefreshCw, Sparkles, Wand2, Save, History, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { readerPushApi } from '@/lib/api';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ax  = axios.create({ baseURL: API, withCredentials: true });

const HEAT_TONES = {
  mild:      'bg-emerald-50 text-emerald-900 border-emerald-200',
  medium:    'bg-amber-50   text-amber-900   border-amber-200',
  hot:       'bg-orange-50  text-orange-900  border-orange-200',
  scorching: 'bg-rose-50    text-rose-900    border-rose-200',
  unknown:   'bg-secondary  text-foreground  border-border',
};

function Bar({ pct }) {
  return (
    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-secondary">
      <div className="h-full bg-primary" style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
    </div>
  );
}

/**
 * Feb-2026 — Reader Push History card at the top of the Shelf
 * Attribution panel. Shows the last 5 `mirror_sync_runs` (time,
 * mode, created/updated/failed) and offers a one-click "Push now"
 * button that fires `POST /api/admin/ai-sync/all?mode=all` and
 * refreshes the history in-place. Ops uses this to see when the
 * shelf was last pushed to the Companion Reader without opening
 * the Indie Reader admin tab.
 */
function ReaderPushHistoryCard() {
  const [rows, setRows]     = useState([]);
  const [busy, setBusy]     = useState(false);
  const [pushing, setPush]  = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await readerPushApi.history(5);
      setRows(r.runs || []);
    } catch (e) {
      // silent — this is a top-of-page decorative card, don't spam toasts
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pushNow = async () => {
    if (!window.confirm('Push the current published shelf to the Companion Reader now?')) return;
    setPush(true);
    try {
      const r = await readerPushApi.pushNow('all');
      if (r?.ok) {
        toast.success(`Pushed shelf → created ${r.created_count}, updated ${r.updated_count}.`);
      } else {
        toast.error(`Push finished with ${r.failed_count || 0} failure(s).`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push failed.');
    } finally { setPush(false); }
  };

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1)  return 'just now';
      if (diffMin < 60) return `${diffMin} min ago`;
      const hrs = Math.round(diffMin / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.round(hrs / 24);
      return `${days}d ago`;
    } catch { return '—'; }
  };

  return (
    <section className="rounded-md border border-border bg-card p-3"
             data-testid="reader-push-history">
      <header className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <History className="h-3.5 w-3.5 text-primary" />
          <div className="text-[12.5px] font-semibold">Reader push history</div>
          <span className="text-[10.5px] text-muted-foreground">last 5 runs</span>
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={load} disabled={busy}
                  data-testid="reader-push-refresh-btn">
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={pushNow} disabled={pushing}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="reader-push-now-btn">
            {pushing ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                      : <Upload className="mr-1 h-3.5 w-3.5" />}
            Push now
          </Button>
        </div>
      </header>
      {rows.length === 0 && !busy && (
        <div className="rounded border border-dashed border-border bg-background/60 px-3 py-3 text-center text-[11px] text-muted-foreground">
          No pushes recorded yet — hit <b>Push now</b> to mirror the current shelf to the Companion Reader.
        </div>
      )}
      {rows.length > 0 && (
        <table className="w-full text-[11.5px]">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="pb-1 text-left">When</th>
              <th className="pb-1 text-left">Trigger</th>
              <th className="pb-1 text-left">Mode</th>
              <th className="pb-1 text-right">Created</th>
              <th className="pb-1 text-right">Updated</th>
              <th className="pb-1 text-right">Failed</th>
              <th className="pb-1 text-right">Duration</th>
              <th className="pb-1 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border"
                  data-testid={`reader-push-row-${r.id}`}>
                <td className="py-1 pr-2">{fmtTime(r.started_at)}</td>
                <td className="py-1 pr-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  {r.trigger || '—'}
                </td>
                <td className="py-1 pr-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  {r.mode || '—'}
                </td>
                <td className="py-1 pr-2 text-right">{r.created_count ?? 0}</td>
                <td className="py-1 pr-2 text-right">{r.updated_count ?? 0}</td>
                <td className={`py-1 pr-2 text-right ${(r.failed_count || 0) > 0 ? 'font-semibold text-rose-700' : ''}`}>
                  {r.failed_count ?? 0}
                </td>
                <td className="py-1 pr-2 text-right text-[10.5px] text-muted-foreground">
                  {r.duration_ms ? `${(r.duration_ms / 1000).toFixed(1)}s` : '—'}
                </td>
                <td className="py-1 text-center">
                  {r.ok ? (
                    <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9.5px] font-medium text-emerald-800">ok</span>
                  ) : (
                    <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[9.5px] font-medium text-rose-800">fail</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default function ShelfAttributionPanel() {
  const [data,   setData]   = useState(null);
  const [busy,   setBusy]   = useState(false);
  const [shelf,  setShelf]  = useState('all');
  const [rec,    setRec]    = useState(null);
  const [targetSize, setTargetSize] = useState(24);
  const [oneClick, setOneClick] = useState({ catalog: null, slots: [], plan: null });

  const load = useCallback(async (which = shelf) => {
    setBusy(true);
    try {
      const r = await ax.get(`/admin/shelf/attribution?shelf=${which}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load attribution.');
    } finally { setBusy(false); }
  }, [shelf]);

  const loadRec = useCallback(async () => {
    setBusy(true);
    try {
      const src = shelf === 'all' ? 'v2' : shelf;
      const r = await ax.get(`/admin/shelf/recommend?shelf=${src}&target_size=${targetSize}`);
      setRec(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recommender failed.');
    } finally { setBusy(false); }
  }, [shelf, targetSize]);

  const snapshot = async () => {
    setBusy(true);
    try {
      const src = shelf === 'all' ? 'v2' : shelf;
      await ax.post(`/admin/shelf/recommend/snapshot?shelf=${src}&target_size=${targetSize}`);
      toast.success('Recommendation saved to history.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Snapshot failed.');
    } finally { setBusy(false); }
  };

  /* Feb-2026 — Recommender → One-Click chaining.
   *
   * Take the current V3 recommendation (heat mix + bucket mix) and
   * populate the One-Click slot rows so ops can tap "Plan" without
   * hand-typing 8 genre/subgenre/count rows. Strategy:
   *   1. For each bucket (dark, contemp, sweet, …) with N slots,
   *      find the matching genre in the catalog by `bucket`.
   *   2. Distribute N across the heats that got any allocation
   *      proportional to `recommended_by_heat.slots`.
   *   3. Emit one slot row per (bucket, heat) pair that ends up ≥ 1
   *      title, picking the genre's `default_heat` when the bucket
   *      appears with an unavailable heat.
   *   4. Give the user a final chance to review — they still have to
   *      hit Plan → Seed.
   */
  const applyRecToOneClick = () => {
    if (!rec?.recommended_by_bucket?.length) {
      toast.error('Run the recommender first.');
      return;
    }
    if (!oneClick.catalog?.genres?.length) {
      toast.error('Genre catalog not loaded yet — retry in a moment.');
      return;
    }
    // Sum of heat slots for proportional split within each bucket.
    const heatRows = (rec.recommended_by_heat || []).filter((h) => h.slots > 0);
    const heatTotal = heatRows.reduce((s, h) => s + h.slots, 0) || 1;
    const nextSlots = [];
    for (const bRow of rec.recommended_by_bucket) {
      if (!bRow.slots) continue;
      // Find the primary genre for this bucket.
      const genre = oneClick.catalog.genres.find(
        (g) => g.bucket === bRow.key,
      ) || oneClick.catalog.genres[0];
      const subgenre = genre.subgenres[0].id;
      if (!heatRows.length) {
        // No heat guidance — everything on the genre default.
        nextSlots.push({ genre: genre.id, subgenre,
                         heat: genre.default_heat, count: bRow.slots });
        continue;
      }
      // Distribute proportionally, floor first, remainder to the top heat.
      let remaining = bRow.slots;
      const alloc = heatRows.map((h) => ({
        heat:  h.key,
        raw:   (h.slots / heatTotal) * bRow.slots,
      }));
      alloc.forEach((a) => {
        a.floor = Math.floor(a.raw);
        remaining -= a.floor;
      });
      // Assign leftover to the highest-weighted heat.
      alloc.sort((a, b) => (b.raw - b.floor) - (a.raw - a.floor));
      for (let i = 0; i < remaining; i += 1) {
        alloc[i % alloc.length].floor += 1;
      }
      for (const a of alloc) {
        if (a.floor <= 0) continue;
        nextSlots.push({ genre: genre.id, subgenre,
                         heat: a.heat, count: a.floor });
      }
    }
    if (!nextSlots.length) {
      toast.error('Recommender returned no allocated slots.');
      return;
    }
    setOneClick((c) => ({ ...c, slots: nextSlots, plan: null }));
    const total = nextSlots.reduce((s, x) => s + x.count, 0);
    toast.success(`Applied V3 plan — ${nextSlots.length} slot rows`
                    + ` (${total} titles). Review then click Plan.`);
    // Scroll the one-click card into view for immediate review.
    setTimeout(() => {
      document.querySelector('[data-testid="shelf-oneclick"]')?.scrollIntoView(
        { behavior: 'smooth', block: 'start' });
    }, 30);
  };

  useEffect(() => { load(shelf); }, [load, shelf]);

  useEffect(() => {
    ax.get('/admin/shelf/genre-catalog')
      .then((r) => setOneClick((c) => ({ ...c, catalog: r.data })))
      .catch(() => {});
  }, []);

  const addSlot = () => {
    const g = oneClick.catalog?.genres?.[0];
    if (!g) return;
    setOneClick((c) => ({ ...c, slots: [...c.slots,
      { genre: g.id, subgenre: g.subgenres[0].id, heat: g.default_heat, count: 4 }] }));
  };
  const updateSlot = (idx, patch) => {
    setOneClick((c) => {
      const next = c.slots.slice();
      next[idx] = { ...next[idx], ...patch };
      if (patch.genre) {
        const g = c.catalog.genres.find((gg) => gg.id === patch.genre);
        if (g) { next[idx].subgenre = g.subgenres[0].id; next[idx].heat = g.default_heat; }
      }
      return { ...c, slots: next };
    });
  };
  const removeSlot = (idx) => setOneClick((c) =>
    ({ ...c, slots: c.slots.filter((_, i) => i !== idx) }));

  const runPlan = async () => {
    if (!oneClick.slots.length) { toast.error('Add at least one slot.'); return; }
    setBusy(true);
    try {
      const r = await ax.post('/admin/shelf/one-click-plan', {
        slots: oneClick.slots, target_size: targetSize,
      });
      setOneClick((c) => ({ ...c, plan: r.data }));
      toast.success(`Planned ${r.data.specs.length} specs — review below.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.detail
                    || e?.response?.data?.detail
                    || 'Plan failed.');
    } finally { setBusy(false); }
  };
  const seedPlan = async () => {
    if (!oneClick.plan?.specs?.length) return;
    if (!window.confirm(`Seed ${oneClick.plan.specs.length} draft manuscripts into the Loom?`)) return;
    setBusy(true);
    try {
      const r = await ax.post('/admin/shelf/one-click-seed', { specs: oneClick.plan.specs });
      toast.success(`Seeded ${r.data.created} manuscripts (${r.data.skipped} skipped).`);
      setOneClick((c) => ({ ...c, plan: null, slots: [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed.');
    } finally { setBusy(false); }
  };

  const backfill = async () => {
    setBusy(true);
    try {
      const r = await ax.post('/admin/shelf/backfill-loom-spec-summary');
      toast.success(`Backfilled ${r.data.updated} book${r.data.updated === 1 ? '' : 's'}`);
      await load(shelf);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed.');
    } finally { setBusy(false); }
  };

  if (!data && busy) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading attribution…
      </div>
    );
  }
  if (!data) return null;

  const maxHeatRev   = Math.max(1, ...data.by_heat.map((r) => r.revenue_cents));
  const maxBucketRev = Math.max(1, ...data.by_bucket.map((r) => r.revenue_cents));
  const topTitles = (data.by_title || []).slice(0, 10);

  return (
    <div className="space-y-4" data-testid="shelf-attribution-panel">
      <ReaderPushHistoryCard />
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.24em] text-muted-foreground">
            <TrendingUp className="h-3.5 w-3.5" /> Shelf attribution
          </div>
          <h2 className="mt-0.5 font-serif text-[22px] leading-tight">
            Which subgenre pays?
          </h2>
          <div className="text-[11.5px] text-muted-foreground">
            {data.totals.orders.toLocaleString()} orders ·{' '}
            <b className="text-foreground">{data.totals.revenue_display}</b>{' '}
            revenue · avg ${(data.totals.avg_order_cents / 100).toFixed(2)}/order
          </div>
        </div>
        <div className="flex items-center gap-1">
          {['all', 'v1', 'v2', 'none'].map((s) => (
            <button key={s} onClick={() => setShelf(s)}
                    data-testid={`shelf-attr-tab-${s}`}
                    className={`rounded-full border px-2.5 py-0.5 text-[10.5px] font-semibold uppercase ${
                      shelf === s
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background hover:bg-secondary'}`}>
              {s}
            </button>
          ))}
          <Button size="sm" variant="outline" onClick={() => load(shelf)} disabled={busy}
                  data-testid="shelf-attr-refresh-btn">
            <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={backfill} disabled={busy}
                  data-testid="shelf-attr-backfill-btn">
            <Sparkles className="mr-1 h-3.5 w-3.5" /> Backfill
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-md border border-border bg-card p-3"
             data-testid="shelf-attr-by-heat">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[12.5px] font-semibold">Revenue by heat level</div>
            <div className="text-[10px] text-muted-foreground">{data.by_heat.length} tiers</div>
          </div>
          <ul className="space-y-2">
            {data.by_heat.map((r) => (
              <li key={r.key}
                  className={`rounded border px-2 py-1.5 ${HEAT_TONES[r.key] || HEAT_TONES.unknown}`}
                  data-testid={`shelf-attr-heat-${r.key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11.5px] font-semibold uppercase tracking-wider">{r.key}</span>
                  <span className="text-[12px] font-bold">{r.revenue_display}</span>
                </div>
                <div className="text-[10.5px] text-muted-foreground">{r.orders} orders</div>
                <Bar pct={(r.revenue_cents / maxHeatRev) * 100} />
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-md border border-border bg-card p-3"
             data-testid="shelf-attr-by-bucket">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[12.5px] font-semibold">Revenue by bucket</div>
            <div className="text-[10px] text-muted-foreground">{data.by_bucket.length} genres</div>
          </div>
          <ul className="space-y-2">
            {data.by_bucket.map((r) => (
              <li key={r.key}
                  className="rounded border border-border bg-secondary/40 px-2 py-1.5"
                  data-testid={`shelf-attr-bucket-${r.key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11.5px] font-semibold uppercase tracking-wider">{r.key}</span>
                  <span className="text-[12px] font-bold">{r.revenue_display}</span>
                </div>
                <div className="text-[10.5px] text-muted-foreground">{r.orders} orders</div>
                <Bar pct={(r.revenue_cents / maxBucketRev) * 100} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="rounded-md border border-primary/40 bg-primary/5 p-3"
               data-testid="shelf-oneclick">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            <Sparkles className="h-4 w-4" /> One-click Shelf V3 seeder
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={addSlot} disabled={busy}
                    data-testid="shelf-oneclick-add-slot">+ slot</Button>
            <Button size="sm" onClick={runPlan} disabled={busy || !oneClick.slots.length}
                    data-testid="shelf-oneclick-plan-btn">
              <Wand2 className="mr-1 h-3.5 w-3.5" /> Plan
            </Button>
            {oneClick.plan?.specs?.length > 0 && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={seedPlan} disabled={busy}
                      data-testid="shelf-oneclick-seed-btn">
                <Save className="mr-1 h-3.5 w-3.5" /> Seed {oneClick.plan.specs.length}
              </Button>
            )}
          </div>
        </div>
        {oneClick.slots.length === 0 && (
          <p className="text-[11px] text-muted-foreground">
            Add slot rows (genre + subgenre + heat + count) then click <b>Plan</b>. Review
            the generated titles below; if you like them, click <b>Seed</b> to mint the
            manuscripts. Drafting is a separate step you'll trigger later.
          </p>
        )}
        {oneClick.slots.length > 0 && oneClick.catalog && (
          <div className="space-y-1.5">
            {oneClick.slots.map((slot, i) => {
              const g = oneClick.catalog.genres.find((gg) => gg.id === slot.genre) || oneClick.catalog.genres[0];
              return (
                <div key={i} className="flex flex-wrap items-center gap-1.5 rounded border border-border bg-background/60 px-2 py-1.5"
                     data-testid={`shelf-oneclick-slot-${i}`}>
                  <select value={slot.genre}
                          onChange={(e) => updateSlot(i, { genre: e.target.value })}
                          data-testid={`shelf-oneclick-genre-${i}`}
                          className="rounded border border-border bg-background px-1.5 py-0.5 text-[11.5px]">
                    {oneClick.catalog.genres.map((gg) => (
                      <option key={gg.id} value={gg.id}>{gg.label}</option>
                    ))}
                  </select>
                  <select value={slot.subgenre}
                          onChange={(e) => updateSlot(i, { subgenre: e.target.value })}
                          data-testid={`shelf-oneclick-subgenre-${i}`}
                          className="rounded border border-border bg-background px-1.5 py-0.5 text-[11.5px]">
                    {g.subgenres.map((sg) => (
                      <option key={sg.id} value={sg.id}>{sg.label}</option>
                    ))}
                  </select>
                  <select value={slot.heat}
                          onChange={(e) => updateSlot(i, { heat: e.target.value })}
                          data-testid={`shelf-oneclick-heat-${i}`}
                          className="rounded border border-border bg-background px-1.5 py-0.5 text-[11.5px]">
                    {oneClick.catalog.heats.map((h) => (
                      <option key={h.id} value={h.id}>{h.label}</option>
                    ))}
                  </select>
                  <input type="number" min={1} max={50} value={slot.count}
                         onChange={(e) => updateSlot(i, { count: parseInt(e.target.value, 10) || 1 })}
                         data-testid={`shelf-oneclick-count-${i}`}
                         className="w-14 rounded border border-border bg-background px-1.5 py-0.5 text-[11.5px]" />
                  <button onClick={() => removeSlot(i)}
                          data-testid={`shelf-oneclick-remove-${i}`}
                          className="ml-auto text-[10.5px] text-muted-foreground hover:text-red-700">
                    remove
                  </button>
                </div>
              );
            })}
            <div className="pt-1 text-[10.5px] text-muted-foreground">
              Total slots: <b>{oneClick.slots.reduce((s, x) => s + (x.count || 0), 0)}</b>
              {' '}/ target {targetSize}
            </div>
          </div>
        )}
        {oneClick.plan?.specs?.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              Planned specs ({oneClick.plan.specs.length}) — review before seeding
            </div>
            <ul className="max-h-64 space-y-1 overflow-y-auto rounded border border-border bg-background/60 p-2"
                data-testid="shelf-oneclick-plan-list">
              {oneClick.plan.specs.map((s, i) => (
                <li key={i} className="text-[11.5px]">
                  <b>{s.title}</b>
                  <span className="ml-1 text-[10px] text-muted-foreground">
                    · {s.bucket} · {s.heat} · {s.subgenre_id}
                  </span>
                  <div className="text-[10.5px] italic text-muted-foreground">
                    {s.logline}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="rounded-md border border-primary/40 bg-primary/5 p-3"
               data-testid="shelf-recommender">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[12.5px] font-semibold">
            <Wand2 className="h-4 w-4" /> Subgenre auto-recommender
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              Target
              <input type="number" min={1} max={200} step={1}
                     value={targetSize}
                     onChange={(e) => setTargetSize(parseInt(e.target.value, 10) || 24)}
                     data-testid="shelf-rec-target-size"
                     className="w-16 rounded border border-border bg-background px-1 py-0.5 text-[11.5px]" />
            </label>
            <Button size="sm" onClick={loadRec} disabled={busy}
                    data-testid="shelf-rec-run-btn">
              <Wand2 className="mr-1 h-3.5 w-3.5" /> Recommend V3
            </Button>
            {rec && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={applyRecToOneClick} disabled={busy}
                        data-testid="shelf-rec-apply-btn"
                        title="Populate the One-Click slot rows with this recommendation.">
                  <Sparkles className="mr-1 h-3.5 w-3.5" /> Apply V3 plan
                </Button>
                <Button size="sm" variant="outline" onClick={snapshot} disabled={busy}
                        data-testid="shelf-rec-snapshot-btn">
                  <Save className="mr-1 h-3.5 w-3.5" /> Save snapshot
                </Button>
              </>
            )}
          </div>
        </div>
        {!rec && (
          <p className="text-[11px] text-muted-foreground">
            Reads Shelf V2 sales and drafts a heat mix for Shelf V3
            weighted by revenue-per-spec. Save a snapshot to lock in an
            ops decision.
          </p>
        )}
        {rec && (
          <div className="space-y-3">
            <p className="rounded bg-background/60 p-2 text-[12.5px] text-foreground/90"
               data-testid="shelf-rec-explanation">
              {rec.explanation}
            </p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  Recommended heat mix
                </div>
                <ul className="space-y-1.5">
                  {rec.recommended_by_heat.map((r) => (
                    <li key={r.key}
                        className={`flex items-center justify-between rounded border px-2 py-1 ${
                          HEAT_TONES[r.key] || HEAT_TONES.unknown}`}
                        data-testid={`shelf-rec-heat-${r.key}`}>
                      <span className="text-[11.5px] font-semibold uppercase tracking-wider">
                        {r.key}
                      </span>
                      <span className="text-[12.5px] font-bold">
                        {r.slots} {r.slots === 1 ? 'title' : 'titles'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  Recommended bucket mix
                </div>
                <ul className="space-y-1.5">
                  {rec.recommended_by_bucket.map((r) => (
                    <li key={r.key}
                        className="flex items-center justify-between rounded border border-border bg-secondary/40 px-2 py-1"
                        data-testid={`shelf-rec-bucket-${r.key}`}>
                      <span className="text-[11.5px] font-semibold uppercase tracking-wider">
                        {r.key}
                      </span>
                      <span className="text-[12.5px] font-bold">
                        {r.slots} {r.slots === 1 ? 'title' : 'titles'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="rounded-md border border-border bg-card p-3"
               data-testid="shelf-attr-top-titles">
        <div className="mb-2 text-[12.5px] font-semibold">Top 10 titles</div>
        <table className="w-full text-[11.5px]">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="pb-1 text-left">Title</th>
              <th className="pb-1 text-left">Bucket</th>
              <th className="pb-1 text-left">Heat</th>
              <th className="pb-1 text-right">Orders</th>
              <th className="pb-1 text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {topTitles.length === 0 && (
              <tr><td colSpan={5} className="pt-2 text-center text-muted-foreground">
                No sales attributed yet — run Backfill after publishing.
              </td></tr>
            )}
            {topTitles.map((t) => (
              <tr key={t.key} className="border-t border-border">
                <td className="py-1 pr-2 font-medium">{t.key}</td>
                <td className="py-1 pr-2 uppercase tracking-wider text-[10px] text-muted-foreground">
                  {t.bucket || '—'}
                </td>
                <td className="py-1 pr-2 uppercase tracking-wider text-[10px] text-muted-foreground">
                  {t.heat || '—'}
                </td>
                <td className="py-1 pr-2 text-right">{t.orders}</td>
                <td className="py-1 text-right font-semibold">{t.revenue_display}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
